/**
 * NodePropertyPanel -- Right sidebar property editor for the workflow builder.
 *
 * Shows type-specific configuration fields when a node is selected.
 * Edits are applied via the onUpdate callback; onDelete removes the node.
 */

import { useMemo, useState, useEffect } from 'react';
import {
  Zap, Plug, GitBranch, ShieldCheck, Repeat, Clock, Bell, Globe, Sparkles,
  Bot, Trash2, X, Plus, Wrench, BookOpen, Code,
} from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { cn } from '@/lib/utils';
import { ExpressionField } from './expression-field';
import { useWorkspaceTools } from '@/lib/use-workspace-tools';
import { useStudioSkills } from '@/lib/use-studio-skills';
import {
  isStudioSkillSelected,
  normalizeStudioSkillSelections,
  toggleStudioSkillSelection,
} from '@/lib/studio-skill-selection';
import { useWorkflows } from '@/lib/use-automate';
import { PromptApplyDialog } from '@/components/studio/prompts/prompt-apply-dialog';
import {
  NODE_TYPE_CONFIG, CRON_PRESETS,
  EVENT_TYPE_LABELS, EVENT_TYPE_GROUPS, EVENT_TYPE_FILTER_FIELDS,
  type NodeType, type EventType, type EventFilterField,
} from '@/lib/mock-automate-data';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// ── Icon map ─────────────────────────────────────────────────────────────

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  zap: Zap,
  bot: Bot,
  plug: Plug,
  'git-branch': GitBranch,
  sparkles: Sparkles,
  'shield-check': ShieldCheck,
  repeat: Repeat,
  clock: Clock,
  globe: Globe,
  bell: Bell,
};

// ── Props ────────────────────────────────────────────────────────────────

export interface NodePropertyPanelProps {
  nodeId: string;
  nodeType: NodeType;
  label: string;
  config: Record<string, unknown>;
  onUpdate: (nodeId: string, label: string, config: Record<string, unknown>) => void;
  onDelete: (nodeId: string) => void;
  onClose: () => void;
}

// Decision 29 (B8): the node has no visibility switches — it always applies
// THE Document visibility rule on the server. A config saved with the retired
// switches loses them here (the normaliser writes only the keys below).
export const RAG_SEARCH_DEFAULT_CONFIG = {
  collection: 'tenant.knowledge_base',
  top_k: 10,
  apply_reranker: true,
  apply_crag_gate: false,
} as const;

function stringConfig(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.trim().length > 0 ? value : fallback;
}

function intConfig(value: unknown, fallback: number, min: number, max: number): number {
  const numberValue = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numberValue)) return fallback;
  return Math.min(max, Math.max(min, Math.trunc(numberValue)));
}

function boolConfig(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

export function normalizeRagSearchNodeConfig(
  rawConfig: Record<string, unknown>,
): Record<string, unknown> {
  return {
    query_template: stringConfig(
      rawConfig.query_template ?? rawConfig.query,
      '',
    ),
    collection: stringConfig(
      rawConfig.collection,
      RAG_SEARCH_DEFAULT_CONFIG.collection,
    ),
    top_k: intConfig(
      rawConfig.top_k ?? rawConfig.topK,
      RAG_SEARCH_DEFAULT_CONFIG.top_k,
      1,
      50,
    ),
    apply_reranker: boolConfig(
      rawConfig.apply_reranker,
      RAG_SEARCH_DEFAULT_CONFIG.apply_reranker,
    ),
    apply_crag_gate: boolConfig(
      rawConfig.apply_crag_gate,
      RAG_SEARCH_DEFAULT_CONFIG.apply_crag_gate,
    ),
  };
}

// ── Component ────────────────────────────────────────────────────────────

export function NodePropertyPanel({
  nodeId,
  nodeType,
  label: initialLabel,
  config: initialConfig,
  onUpdate,
  onDelete,
  onClose,
}: NodePropertyPanelProps) {
  const [label, setLabel] = useState(initialLabel);
  const [config, setConfig] = useState<Record<string, unknown>>({ ...initialConfig });
  const [promptApplyTarget, setPromptApplyTarget] = useState<'agent' | 'llm' | null>(null);
  const {
    data: studioSkillsResp,
    isLoading: studioSkillsLoading,
    isError: studioSkillsError,
  } = useStudioSkills({ activeOnly: true, scope: 'all' });
  const studioSkills = useMemo(() => studioSkillsResp?.items ?? [], [studioSkillsResp]);

  // Sync when selection changes
  useEffect(() => {
    setLabel(initialLabel);
    setConfig({ ...initialConfig });
  }, [nodeId, initialLabel, initialConfig]);

  const cfg = NODE_TYPE_CONFIG[nodeType];
  const Icon = ICON_MAP[cfg.icon] ?? Zap;

  const updateConfig = (key: string, value: unknown) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  };

  const handleApply = () => {
    const normalizedConfig = nodeType === 'rag_search'
      ? normalizeRagSearchNodeConfig(config)
      : { ...config };
    if (Array.isArray(config.skills)) {
      normalizedConfig.skills = normalizeStudioSkillSelections(
        config.skills.filter((skill): skill is string => typeof skill === 'string'),
        studioSkills,
      );
    }
    onUpdate(nodeId, label, normalizedConfig);
  };

  // ── Type-switched property forms ──────────────────────────────────────

  // Per-event filter sub-form. Renders field types described by
  // EVENT_TYPE_FILTER_FIELDS so each event type gets exactly the
  // filters that make sense for it (e.g. mailbox + from-pattern for
  // incoming_email; severity multiselect for alarm_triggered).
  const renderEventFilterField = (field: EventFilterField) => {
    const filter = (config.eventFilter as Record<string, unknown> | undefined) ?? {};
    const setFilterValue = (next: Record<string, unknown>) =>
      updateConfig('eventFilter', { ...filter, ...next });
    const value = filter[field.key];

    switch (field.kind) {
      case 'text':
        return (
          <FieldGroup
            key={field.key}
            label={field.label}
            help={field.helper ? <span className="text-[9px] text-muted-foreground">{field.helper}</span> : undefined}
          >
            <Input
              className="h-8 text-xs"
              value={(value as string) ?? ''}
              onChange={(e) => setFilterValue({ [field.key]: e.target.value })}
              placeholder={field.placeholder}
            />
          </FieldGroup>
        );
      case 'number':
        return (
          <FieldGroup key={field.key} label={field.label}>
            <Input
              className="h-8 text-xs"
              type="number"
              value={(value as number | string) ?? ''}
              onChange={(e) => {
                const n = e.target.value === '' ? undefined : Number(e.target.value);
                setFilterValue({ [field.key]: n });
              }}
              placeholder={field.placeholder}
            />
            {field.helper && <p className="text-[9px] text-muted-foreground mt-1">{field.helper}</p>}
          </FieldGroup>
        );
      case 'select':
        return (
          <FieldGroup key={field.key} label={field.label}>
            <Select
              value={(value as string) ?? ''}
              onValueChange={(v) => setFilterValue({ [field.key]: v })}
            >
              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select..." /></SelectTrigger>
              <SelectContent>
                {field.options?.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {field.helper && <p className="text-[9px] text-muted-foreground mt-1">{field.helper}</p>}
          </FieldGroup>
        );
      case 'boolean':
        return (
          <FieldGroup key={field.key} label={field.label}>
            <label className="inline-flex items-center gap-1.5 text-xs">
              <input
                type="checkbox"
                className="h-3 w-3 rounded"
                checked={Boolean(value)}
                onChange={(e) => setFilterValue({ [field.key]: e.target.checked })}
              />
              <span className="text-muted-foreground">{field.placeholder ?? 'Required'}</span>
            </label>
            {field.helper && <p className="text-[9px] text-muted-foreground mt-1">{field.helper}</p>}
          </FieldGroup>
        );
      case 'multiselect': {
        const arr = Array.isArray(value) ? (value as string[]) : [];
        return (
          <FieldGroup key={field.key} label={field.label}>
            <div className="space-y-1 max-h-[120px] overflow-auto border rounded-md p-1.5">
              {field.options?.map((o) => {
                const checked = arr.includes(o.value);
                return (
                  <label
                    key={o.value}
                    className={cn(
                      'flex items-center gap-1.5 px-2 py-1 rounded text-[10px] cursor-pointer',
                      checked ? 'bg-primary/10 text-primary' : 'hover:bg-muted/50',
                    )}
                  >
                    <input
                      type="checkbox"
                      className="h-3 w-3 rounded"
                      checked={checked}
                      onChange={() => {
                        const next = checked ? arr.filter((x) => x !== o.value) : [...arr, o.value];
                        setFilterValue({ [field.key]: next });
                      }}
                    />
                    <span>{o.label}</span>
                  </label>
                );
              })}
            </div>
            {field.helper && <p className="text-[9px] text-muted-foreground mt-1">{field.helper}</p>}
          </FieldGroup>
        );
      }
      default:
        return null;
    }
  };

  const renderTriggerProps = () => {
    const triggerType = (config.triggerType as string) ?? 'manual';
    const eventType = (config.eventType as string) ?? '';
    const eventFilters = eventType
      ? EVENT_TYPE_FILTER_FIELDS[eventType as EventType] ?? []
      : [];
    return (
      <>
        <FieldGroup label="Trigger Type">
          <Select
            value={triggerType}
            onValueChange={(v) => updateConfig('triggerType', v)}
          >
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="scheduled">Scheduled (Cron)</SelectItem>
              <SelectItem value="event">Event</SelectItem>
              <SelectItem value="webhook">Webhook</SelectItem>
              <SelectItem value="manual">Manual</SelectItem>
            </SelectContent>
          </Select>
        </FieldGroup>

        {/* ── Scheduled ─────────────────────────────────────────── */}
        {triggerType === 'scheduled' && (
          <>
            <FieldGroup label="Cron Expression">
              <Input
                className="h-8 text-xs font-mono"
                value={(config.cron as string) ?? ''}
                onChange={(e) => updateConfig('cron', e.target.value)}
                placeholder="0 6 * * *"
              />
            </FieldGroup>
            <FieldGroup label="Presets">
              <Select
                value={(config.cron as string) ?? ''}
                onValueChange={(v) => updateConfig('cron', v)}
              >
                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Quick select..." /></SelectTrigger>
                <SelectContent>
                  {CRON_PRESETS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldGroup>
            <FieldGroup label="Timezone">
              <Input
                className="h-8 text-xs font-mono"
                value={(config.cronTimezone as string) ?? 'UTC'}
                onChange={(e) => updateConfig('cronTimezone', e.target.value)}
                placeholder="UTC"
              />
              <p className="text-[9px] text-muted-foreground mt-1">
                IANA timezone (e.g. America/Chicago). Default UTC.
              </p>
            </FieldGroup>
          </>
        )}

        {/* ── Event (with per-event filter sub-form) ────────────── */}
        {triggerType === 'event' && (
          <>
            <FieldGroup label="Event Type">
              <Select
                value={eventType}
                onValueChange={(v) => {
                  // Reset filter block when event changes — old filter
                  // keys don't apply to a different event shape.
                  updateConfig('eventType', v);
                  updateConfig('eventFilter', {});
                }}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Select event..." />
                </SelectTrigger>
                <SelectContent>
                  {EVENT_TYPE_GROUPS.map((group) => (
                    <div key={group.label}>
                      <p className="px-2 pt-1 pb-0.5 text-[9px] font-semibold uppercase text-muted-foreground tracking-wider">
                        {group.label}
                      </p>
                      {group.events.map((ev) => (
                        <SelectItem key={ev} value={ev}>{EVENT_TYPE_LABELS[ev]}</SelectItem>
                      ))}
                    </div>
                  ))}
                </SelectContent>
              </Select>
            </FieldGroup>

            {/* Per-event filter block (#F3) */}
            {eventType && eventFilters.length > 0 && (
              <>
                <Separator />
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                  Filter — fire only when…
                </p>
                {eventFilters.map(renderEventFilterField)}
              </>
            )}
            {eventType && eventFilters.length === 0 && (
              <p className="text-[10px] text-muted-foreground italic">
                This event type has no additional filters; the workflow fires on every occurrence.
              </p>
            )}
          </>
        )}

        {/* ── Webhook (#F4) ─────────────────────────────────────── */}
        {triggerType === 'webhook' && (
          <>
            <FieldGroup
              label="Webhook URL"
              help={<span className="text-[9px] text-muted-foreground">Generated when the workflow is saved. Use this URL to trigger the workflow from external systems.</span>}
            >
              <Input
                className="h-8 text-[10px] font-mono"
                value={(config.webhookUrl as string) ?? '/api/v1/automate-webhooks/<workflow-id>'}
                readOnly
              />
            </FieldGroup>
            <FieldGroup label="Allowed Method">
              <Select
                value={(config.webhookMethod as string) ?? 'POST'}
                onValueChange={(v) => updateConfig('webhookMethod', v)}
              >
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="POST">POST</SelectItem>
                  <SelectItem value="PUT">PUT</SelectItem>
                  <SelectItem value="PATCH">PATCH</SelectItem>
                  <SelectItem value="GET">GET</SelectItem>
                </SelectContent>
              </Select>
            </FieldGroup>
            <FieldGroup label="Authentication">
              <Select
                value={(config.webhookAuth as string) ?? 'shared_secret'}
                onValueChange={(v) => updateConfig('webhookAuth', v)}
              >
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (open URL)</SelectItem>
                  <SelectItem value="shared_secret">Shared secret header</SelectItem>
                  <SelectItem value="hmac">HMAC signature</SelectItem>
                  <SelectItem value="bearer">Bearer token</SelectItem>
                </SelectContent>
              </Select>
            </FieldGroup>
            {(config.webhookAuth as string) === 'shared_secret' && (
              <FieldGroup label="Header name">
                <Input
                  className="h-8 text-xs font-mono"
                  value={(config.webhookSecretHeader as string) ?? 'X-Webhook-Secret'}
                  onChange={(e) => updateConfig('webhookSecretHeader', e.target.value)}
                  placeholder="X-Webhook-Secret"
                />
              </FieldGroup>
            )}
            {(config.webhookAuth as string) === 'hmac' && (
              <FieldGroup label="HMAC algorithm">
                <Select
                  value={(config.webhookHmacAlg as string) ?? 'sha256'}
                  onValueChange={(v) => updateConfig('webhookHmacAlg', v)}
                >
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sha256">SHA-256</SelectItem>
                    <SelectItem value="sha512">SHA-512</SelectItem>
                  </SelectContent>
                </Select>
              </FieldGroup>
            )}
            <FieldGroup label="Validate JSON body">
              <label className="inline-flex items-center gap-1.5 text-xs">
                <input
                  type="checkbox"
                  className="h-3 w-3 rounded"
                  checked={Boolean(config.webhookValidateJson)}
                  onChange={(e) => updateConfig('webhookValidateJson', e.target.checked)}
                />
                <span className="text-muted-foreground">Reject non-JSON requests</span>
              </label>
            </FieldGroup>
          </>
        )}

        {/* ── Manual ────────────────────────────────────────────── */}
        {triggerType === 'manual' && (
          <p className="text-[10px] text-muted-foreground italic px-1">
            This workflow runs when a user clicks "Run" on the workflow page.
            Manual run input parameters are configured per-workflow on the run dialog.
          </p>
        )}
      </>
    );
  };

  const renderAgentProps = () => (
    <>
      {/* Agent Source: pre-built or configure inline */}
      <FieldGroup label="Agent Source">
        <Select
          value={(config.agentSource as string) ?? 'inline'}
          onValueChange={(v) => updateConfig('agentSource', v)}
        >
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="inline">Configure Inline (Recommended)</SelectItem>
            <SelectItem value="preset">Use Pre-built Agent</SelectItem>
          </SelectContent>
        </Select>
      </FieldGroup>

      {/* Pre-built agent picker (when agentSource = 'preset') */}
      {(config.agentSource as string) === 'preset' && (
        <FieldGroup label="Select Agent">
          <Select
            value={(config.agentId as string) ?? ''}
            onValueChange={(v) => updateConfig('agentId', v)}
          >
            <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select agent..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="maintenance-intelligence">Maintenance Intelligence</SelectItem>
              <SelectItem value="troubleshooting">Troubleshooting</SelectItem>
              <SelectItem value="incident-analysis">Incident Analysis</SelectItem>
              <SelectItem value="documentation-sop">Documentation / SOP</SelectItem>
              <SelectItem value="compliance-monitoring">Compliance Monitor</SelectItem>
              <SelectItem value="safety-loto">Safety & LOTO</SelectItem>
              <SelectItem value="work-order-planner">Work Order Planner</SelectItem>
              <SelectItem value="parts-inventory">Parts & Inventory</SelectItem>
              <SelectItem value="reliability">Reliability</SelectItem>
              <SelectItem value="shift-handover">Shift Handover</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-[9px] text-muted-foreground mt-1">Uses the agent's pre-configured tools, skills, and prompts</p>
        </FieldGroup>
      )}

      <Separator />

      {/* Instructions / System Prompt */}
      <FieldGroup label="Instructions">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mb-1 h-7 w-full text-[10px] gap-1"
          onClick={() => setPromptApplyTarget('agent')}
        >
          <Sparkles className="h-3 w-3" />
          Apply Studio prompt
        </Button>
        <Textarea
          className="text-xs min-h-[80px]"
          value={(config.prompt as string) ?? ''}
          onChange={(e) => updateConfig('prompt', e.target.value)}
          placeholder="What should this agent do in this workflow step? Be specific about expected inputs, processing, and output format."
        />
        {typeof config.promptTemplateTitle === 'string' && config.promptTemplateTitle.length > 0 && (
          <p className="text-[9px] text-muted-foreground mt-1">
            Applied from <span className="font-medium">{config.promptTemplateTitle}</span>
            {typeof config.promptTemplateCommand === 'string' && config.promptTemplateCommand.length > 0 ? <span className="font-mono"> /{config.promptTemplateCommand}</span> : null}
          </p>
        )}
        <p className="text-[9px] text-muted-foreground mt-1">Define the agent's task for this specific workflow step</p>
      </FieldGroup>

      {/* Model */}
      <FieldGroup label="Model">
        <Select
          value={(config.model as string) ?? 'default'}
          onValueChange={(v) => updateConfig('model', v)}
        >
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="default">Argus Default (Qwen 72B AWQ)</SelectItem>
            <SelectItem value="fast">Argus Fast (Llama 3.2 3B)</SelectItem>
            <SelectItem value="code">Argus Code (Qwen Coder 32B)</SelectItem>
            <SelectItem value="precise">Precise (72B, temp 0.1)</SelectItem>
          </SelectContent>
        </Select>
      </FieldGroup>

      <Separator />

      {/* Tools — inline picker (#F6: pulls live tools from
          /workspace/tools, no more hardcoded IQ/SAP catalog) */}
      <FieldGroup label="Tools">
        <p className="text-[9px] text-muted-foreground mb-1.5">Tools this agent can call in this step. Manage them in Connections.</p>
        <ToolsAgentPicker
          selected={(config.tools as string[]) ?? []}
          onChange={(next) => updateConfig('tools', next)}
        />
      </FieldGroup>

      {/* Skills */}
      <FieldGroup label="Skills">
        {studioSkillsLoading ? (
          <p className="text-[10px] text-muted-foreground py-2">Loading Studio skills...</p>
        ) : studioSkillsError ? (
          <div className="border border-destructive/30 rounded-md p-3 text-center">
            <BookOpen className="h-4 w-4 mx-auto text-destructive/60 mb-1" />
            <p className="text-[10px] text-destructive">Studio skills could not be loaded.</p>
            <p className="text-[9px] text-muted-foreground mt-1">
              Verify the Studio Skills catalog before saving skill changes.
            </p>
            <Link to="/studio/skills" className="text-[10px] text-primary hover:underline">
              Open Studio Skills
            </Link>
          </div>
        ) : studioSkills.length === 0 ? (
          <div className="border border-dashed rounded-md p-3 text-center">
            <BookOpen className="h-4 w-4 mx-auto text-muted-foreground/40 mb-1" />
            <p className="text-[10px] text-muted-foreground">No Studio skills available.</p>
            <Link to="/studio/skills" className="text-[10px] text-primary hover:underline">
              Open Studio Skills
            </Link>
          </div>
        ) : (
          <div className="space-y-1 max-h-[140px] overflow-auto border rounded-md p-1.5">
            {studioSkills.map(skill => {
            const skills = (config.skills as string[]) ?? [];
            const isSelected = isStudioSkillSelected(skills, skill, studioSkills);
            return (
              <label key={`${skill.scope}:${skill.id}`} className={cn('flex items-start gap-1.5 px-2 py-1 rounded text-[10px] cursor-pointer transition-colors', isSelected ? 'bg-purple-500/10 text-purple-600 dark:text-purple-400' : 'hover:bg-muted/50')}>
                <input type="checkbox" className="h-3 w-3 rounded" checked={isSelected} onChange={() => {
                  const next = toggleStudioSkillSelection(skills, skill, studioSkills);
                  updateConfig('skills', next);
                }} />
                <span className="min-w-0 flex-1">
                  <span className="block font-medium truncate">{skill.displayName || skill.name}</span>
                  <span className="block font-mono text-[9px] text-muted-foreground/70 truncate">{skill.name}</span>
                </span>
                <span className="rounded border px-1 text-[8px] uppercase text-muted-foreground">{skill.scope}</span>
              </label>
            );
            })}
          </div>
        )}
      </FieldGroup>

      {/* Knowledge Sources */}
      <FieldGroup label="Knowledge Sources">
        <Select
          value={(config.knowledgeScope as string) ?? 'all'}
          onValueChange={(v) => updateConfig('knowledgeScope', v)}
        >
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Library Documents</SelectItem>
            <SelectItem value="uploaded">Uploaded Documents Only</SelectItem>
            <SelectItem value="wiki">Wiki Articles Only</SelectItem>
            <SelectItem value="external">External Sources Only</SelectItem>
            <SelectItem value="none">No Knowledge Access</SelectItem>
          </SelectContent>
        </Select>
      </FieldGroup>

      <Separator />

      {/* Execution Settings */}
      <FieldGroup label="Max Steps">
        <Input
          className="h-8 text-xs"
          type="number"
          min={1}
          max={50}
          value={(config.maxSteps as number) ?? 10}
          onChange={(e) => updateConfig('maxSteps', parseInt(e.target.value) || 10)}
        />
        <p className="text-[9px] text-muted-foreground mt-1">Maximum tool calls before the agent must respond</p>
      </FieldGroup>

      <FieldGroup label={`Temperature: ${((config.temperature as number) ?? 0.7).toFixed(1)}`}>
        <Slider
          min={0}
          max={1}
          step={0.1}
          value={[(config.temperature as number) ?? 0.7]}
          onValueChange={([v]) => updateConfig('temperature', v)}
        />
        <p className="text-[9px] text-muted-foreground mt-1">Lower = more precise, Higher = more creative</p>
      </FieldGroup>

      {/* Memory */}
      <FieldGroup label="Memory">
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-muted-foreground">Include workflow context</span>
          <Switch
            checked={(config.includeContext as boolean) ?? true}
            onCheckedChange={(v) => updateConfig('includeContext', v)}
          />
        </div>
        <div className="flex items-center justify-between mt-1">
          <span className="text-[10px] text-muted-foreground">Include previous step results</span>
          <Switch
            checked={(config.includePrevSteps as boolean) ?? true}
            onCheckedChange={(v) => updateConfig('includePrevSteps', v)}
          />
        </div>
      </FieldGroup>

      {/* Approval Policy */}
      <FieldGroup label="Approval Policy">
        <Select
          value={(config.approvalPolicy as string) ?? 'auto'}
          onValueChange={(v) => updateConfig('approvalPolicy', v)}
        >
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="auto">Auto-approve all actions</SelectItem>
            <SelectItem value="require_writes">Require approval for write operations</SelectItem>
            <SelectItem value="require_all">Require approval for all tool calls</SelectItem>
          </SelectContent>
        </Select>
      </FieldGroup>

      {/* Output Format */}
      <FieldGroup label="Output Format">
        <Select
          value={(config.outputFormat as string) ?? 'text'}
          onValueChange={(v) => updateConfig('outputFormat', v)}
        >
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="text">Free Text (Markdown)</SelectItem>
            <SelectItem value="json">Structured JSON</SelectItem>
            <SelectItem value="report">Report Format</SelectItem>
          </SelectContent>
        </Select>
      </FieldGroup>
    </>
  );

  const renderToolProps = () => (
    <>
      {/* Live tool picker (#F10) — replaces the free-text Tool input
          that required typing the exact tool name from memory. */}
      <FieldGroup
        label="Tool"
        help={<span className="text-[9px] text-muted-foreground">Pulled from registered Connections + built-in tools.</span>}
      >
        <ToolSinglePicker
          value={(config.tool as string) ?? ''}
          onChange={(v) => updateConfig('tool', v)}
        />
      </FieldGroup>
      <FieldGroup label="Parameters (JSON)">
        <Textarea
          className="text-xs font-mono min-h-[60px]"
          value={typeof config.params === 'object' ? JSON.stringify(config.params, null, 2) : (config.params as string) ?? '{}'}
          onChange={(e) => {
            try {
              updateConfig('params', JSON.parse(e.target.value));
            } catch {
              updateConfig('params', e.target.value);
            }
          }}
          placeholder='{ "days": 30 }'
        />
        <p className="text-[9px] text-muted-foreground mt-1">
          Use <code className="text-[9px]">{'{{ $input.json.field }}'}</code> expressions to pull values from upstream nodes.
        </p>
      </FieldGroup>
      <FieldGroup label="Timeout (seconds)">
        <Input
          className="h-8 text-xs"
          type="number"
          min={1}
          max={300}
          value={(config.timeout as number) ?? 30}
          onChange={(e) => updateConfig('timeout', parseInt(e.target.value) || 30)}
        />
      </FieldGroup>
    </>
  );

  const renderConditionProps = () => {
    type Rule = {
      field: string;
      operator: string;
      value: string;
      valueType: 'string' | 'number' | 'boolean' | 'date';
      caseSensitive?: boolean;
    };
    const combinator = (config.combinator as 'and' | 'or') ?? 'and';
    const rules: Rule[] =
      (config.rules as Rule[] | undefined) ??
      (config.field !== undefined
        // Migrate legacy single-rule config -> rules array
        ? [{
            field: (config.field as string) ?? '',
            operator: (config.operator as string) ?? 'eq',
            value: (config.value as string) ?? '',
            valueType: 'string' as const,
            caseSensitive: false,
          }]
        : [{ field: '', operator: 'eq', value: '', valueType: 'string' as const }]);

    const updateRule = (i: number, next: Partial<Rule>) => {
      const updated = rules.map((r, j) => (j === i ? { ...r, ...next } : r));
      updateConfig('rules', updated);
    };
    const addRule = () =>
      updateConfig('rules', [
        ...rules,
        { field: '', operator: 'eq', value: '', valueType: 'string' as const },
      ]);
    const removeRule = (i: number) =>
      updateConfig('rules', rules.filter((_, j) => j !== i));

    return (
      <>
        <FieldGroup label="Combinator">
          <Select value={combinator} onValueChange={(v) => updateConfig('combinator', v)}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="and">All rules must match (AND)</SelectItem>
              <SelectItem value="or">Any rule must match (OR)</SelectItem>
            </SelectContent>
          </Select>
        </FieldGroup>

        <div className="space-y-3">
          {rules.map((rule, i) => (
            <div
              key={i}
              className="border rounded-md p-2 space-y-2 bg-muted/20 relative"
            >
              <div className="flex items-center justify-between">
                <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Rule {i + 1}
                </span>
                {rules.length > 1 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 -my-1"
                    title="Remove rule"
                    onClick={() => removeRule(i)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
              <FieldGroup label="Field">
                <ExpressionField
                  value={rule.field}
                  onChange={(v) => updateRule(i, { field: v })}
                  placeholder="severity or items[0].status"
                />
              </FieldGroup>
              <FieldGroup label="Type">
                <Select
                  value={rule.valueType}
                  onValueChange={(v) => updateRule(i, { valueType: v as Rule['valueType'] })}
                >
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="string">String</SelectItem>
                    <SelectItem value="number">Number</SelectItem>
                    <SelectItem value="boolean">Boolean</SelectItem>
                    <SelectItem value="date">Date</SelectItem>
                  </SelectContent>
                </Select>
              </FieldGroup>
              <FieldGroup label="Operator">
                <Select
                  value={rule.operator}
                  onValueChange={(v) => updateRule(i, { operator: v })}
                >
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="eq">Equals</SelectItem>
                    <SelectItem value="neq">Not equals</SelectItem>
                    {(rule.valueType === 'number' || rule.valueType === 'date') && (
                      <>
                        <SelectItem value="gt">Greater than</SelectItem>
                        <SelectItem value="lt">Less than</SelectItem>
                        <SelectItem value="gte">Greater or equal</SelectItem>
                        <SelectItem value="lte">Less or equal</SelectItem>
                      </>
                    )}
                    {rule.valueType === 'string' && (
                      <>
                        <SelectItem value="contains">Contains</SelectItem>
                        <SelectItem value="not_contains">Does not contain</SelectItem>
                        <SelectItem value="starts_with">Starts with</SelectItem>
                        <SelectItem value="ends_with">Ends with</SelectItem>
                        <SelectItem value="regex">Regex match</SelectItem>
                      </>
                    )}
                    <SelectItem value="exists">Is present</SelectItem>
                    <SelectItem value="not_exists">Is missing</SelectItem>
                  </SelectContent>
                </Select>
              </FieldGroup>
              {!['exists', 'not_exists'].includes(rule.operator) && (
                <FieldGroup label="Value">
                  <ExpressionField
                    value={rule.value}
                    onChange={(v) => updateRule(i, { value: v })}
                    placeholder={rule.valueType === 'number' ? '42' : rule.valueType === 'boolean' ? 'true' : rule.valueType === 'date' ? '2026-05-07T00:00:00Z' : 'high'}
                  />
                </FieldGroup>
              )}
              {rule.valueType === 'string' && ['contains', 'not_contains', 'starts_with', 'ends_with', 'regex'].includes(rule.operator) && (
                <FieldGroup label="Case sensitive">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-muted-foreground">Match case exactly</span>
                    <Switch
                      checked={Boolean(rule.caseSensitive)}
                      onCheckedChange={(v) => updateRule(i, { caseSensitive: v })}
                    />
                  </div>
                </FieldGroup>
              )}
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            className="w-full h-7 text-xs"
            onClick={addRule}
          >
            <Plus className="h-3 w-3 mr-1" /> Add rule
          </Button>
        </div>
      </>
    );
  };

  const renderAiRouterProps = () => {
    type Cat = { label: string; description?: string };
    // Migrate legacy string[] -> Cat[] on the fly
    const rawCategories = config.categories;
    const categories: Cat[] = Array.isArray(rawCategories)
      ? rawCategories.map((c) =>
          typeof c === 'string' ? { label: c } : (c as Cat),
        )
      : [];
    return (
      <>
        <FieldGroup label="Classification Prompt">
          <Textarea
            className="text-xs min-h-[60px]"
            value={(config.prompt as string) ?? ''}
            onChange={(e) => updateConfig('prompt', e.target.value)}
            placeholder="Classify the input intent into one of the categories..."
          />
          <p className="text-[9px] text-muted-foreground mt-1">
            Tip: include the input text via <code className="text-[9px]">{'{{ $input.json.text }}'}</code>.
          </p>
        </FieldGroup>
        <FieldGroup label="Model">
          <Select
            value={(config.model as string) ?? 'fast'}
            onValueChange={(v) => updateConfig('model', v)}
          >
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="fast">Fast (Llama 3.2 3B) — recommended for routing</SelectItem>
              <SelectItem value="default">Default (Qwen 72B AWQ)</SelectItem>
              <SelectItem value="precise">Precise (72B, temp 0.1)</SelectItem>
            </SelectContent>
          </Select>
        </FieldGroup>
        <FieldGroup label="Categories (with optional descriptions)">
          <div className="space-y-1.5">
            {categories.map((cat, i) => (
              <div key={i} className="border rounded-md p-1.5 space-y-1 bg-muted/20">
                <div className="flex gap-1">
                  <Input
                    className="h-7 text-xs flex-1"
                    value={cat.label}
                    onChange={(e) => {
                      const updated = categories.map((c, j) =>
                        j === i ? { ...c, label: e.target.value } : c,
                      );
                      updateConfig('categories', updated);
                    }}
                    placeholder={`Category ${i + 1} label`}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 flex-shrink-0"
                    title="Remove category"
                    onClick={() => updateConfig('categories', categories.filter((_, j) => j !== i))}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
                <Input
                  className="h-6 text-[10px]"
                  value={cat.description ?? ''}
                  onChange={(e) => {
                    const updated = categories.map((c, j) =>
                      j === i ? { ...c, description: e.target.value } : c,
                    );
                    updateConfig('categories', updated);
                  }}
                  placeholder="Description for the LLM (optional)"
                />
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              className="w-full h-7 text-xs"
              onClick={() => updateConfig('categories', [...categories, { label: '' }])}
            >
              <Plus className="h-3 w-3 mr-1" /> Add category
            </Button>
          </div>
        </FieldGroup>
        <FieldGroup label={`Confidence Threshold: ${((config.threshold as number) ?? 0.7).toFixed(2)}`}>
          <Slider
            min={0}
            max={1}
            step={0.05}
            value={[(config.threshold as number) ?? 0.7]}
            onValueChange={([v]) => updateConfig('threshold', v)}
          />
          <p className="text-[9px] text-muted-foreground mt-1">
            If the model's confidence is below this, the fallback action below is taken.
          </p>
        </FieldGroup>
        <FieldGroup label="Fallback when below threshold">
          <Select
            value={(config.fallbackAction as string) ?? 'default_category'}
            onValueChange={(v) => updateConfig('fallbackAction', v)}
          >
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="default_category">Send to a default category</SelectItem>
              <SelectItem value="error">Fail the workflow</SelectItem>
              <SelectItem value="ask_clarification">Ask user to clarify</SelectItem>
            </SelectContent>
          </Select>
        </FieldGroup>
        {(config.fallbackAction as string) === 'default_category' && (
          <FieldGroup label="Default category">
            <Select
              value={(config.defaultCategory as string) ?? ''}
              onValueChange={(v) => updateConfig('defaultCategory', v)}
            >
              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Pick a category..." /></SelectTrigger>
              <SelectContent>
                {categories
                  .filter((c) => c.label.trim().length > 0)
                  .map((c, i) => (
                    <SelectItem key={i} value={c.label}>{c.label}</SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </FieldGroup>
        )}
      </>
    );
  };

  const renderApprovalProps = () => {
    const observers = (config.observers as string[]) ?? [];
    return (
      <>
        <FieldGroup label="Approval Level">
          <Select
            value={(config.level as string) ?? 'single'}
            onValueChange={(v) => updateConfig('level', v)}
          >
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="single">Single Approval (any authorised user)</SelectItem>
              <SelectItem value="supervisor">Supervisor Approval (role)</SelectItem>
              <SelectItem value="two_person">Two-Person Rule (two independent approvals)</SelectItem>
            </SelectContent>
          </Select>
        </FieldGroup>
        <FieldGroup label="Approver (user or role)">
          <Input
            className="h-8 text-xs"
            value={(config.approver as string) ?? ''}
            onChange={(e) => updateConfig('approver', e.target.value)}
            placeholder="supervisor@company.com or @maintenance-leads"
          />
        </FieldGroup>
        <FieldGroup
          label="Approval card title"
          help={<span className="text-[9px] text-muted-foreground">What reviewers see in the Approvals queue.</span>}
        >
          <ExpressionField
            value={(config.cardTitle as string) ?? ''}
            onChange={(v) => updateConfig('cardTitle', v)}
            placeholder='Approve {{ $node["Agent"].json.action }}?'
          />
        </FieldGroup>
        <FieldGroup
          label="Summary (shown to reviewer)"
          help={<span className="text-[9px] text-muted-foreground">Markdown supported. Pull data from upstream nodes via expressions.</span>}
        >
          <ExpressionField
            value={(config.summary as string) ?? ''}
            onChange={(v) => updateConfig('summary', v)}
            placeholder={'Action: {{ $node["Agent"].json.action }}\nReason: {{ $node["Agent"].json.reason }}'}
            multiline
          />
        </FieldGroup>
        <FieldGroup label="Observers / CC">
          <div className="space-y-1">
            {observers.map((o, i) => (
              <div key={i} className="flex gap-1">
                <Input
                  className="h-7 text-xs flex-1"
                  value={o}
                  onChange={(e) => {
                    const updated = [...observers];
                    updated[i] = e.target.value;
                    updateConfig('observers', updated);
                  }}
                  placeholder="user@company.com"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 flex-shrink-0"
                  title="Remove observer"
                  onClick={() => updateConfig('observers', observers.filter((_, j) => j !== i))}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              className="w-full h-7 text-xs"
              onClick={() => updateConfig('observers', [...observers, ''])}
            >
              <Plus className="h-3 w-3 mr-1" /> Add observer
            </Button>
          </div>
          <p className="text-[9px] text-muted-foreground mt-1">Notified but cannot approve.</p>
        </FieldGroup>
        <FieldGroup label="Timeout (minutes, 0 = no timeout)">
          <Input
            className="h-8 text-xs"
            type="number"
            min={0}
            max={10080}
            value={(config.timeout as number) ?? 60}
            onChange={(e) => updateConfig('timeout', parseInt(e.target.value) || 0)}
          />
        </FieldGroup>
        <FieldGroup label="On timeout">
          <Select
            value={(config.onTimeout as string) ?? 'fail'}
            onValueChange={(v) => updateConfig('onTimeout', v)}
          >
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="fail">Fail the workflow</SelectItem>
              <SelectItem value="auto_approve">Auto-approve (use cautiously)</SelectItem>
              <SelectItem value="auto_reject">Auto-reject</SelectItem>
              <SelectItem value="escalate">Escalate to next level</SelectItem>
            </SelectContent>
          </Select>
        </FieldGroup>
      </>
    );
  };

  const renderLoopProps = () => (
    <>
      <FieldGroup label="Loop Type">
        <Select
          value={(config.type as string) ?? 'iterate'}
          onValueChange={(v) => updateConfig('type', v)}
        >
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="iterate">Iterate Collection</SelectItem>
            <SelectItem value="repeat">Repeat N Times</SelectItem>
            <SelectItem value="while">While Condition</SelectItem>
          </SelectContent>
        </Select>
      </FieldGroup>
      {config.type === 'iterate' || !config.type ? (
        <FieldGroup label="Collection">
          <Input
            className="h-8 text-xs font-mono"
            value={(config.collection as string) ?? ''}
            onChange={(e) => updateConfig('collection', e.target.value)}
            placeholder="output.items"
          />
        </FieldGroup>
      ) : config.type === 'repeat' ? (
        <FieldGroup label="Repeat Count">
          <Input
            className="h-8 text-xs"
            type="number"
            min={1}
            max={1000}
            value={(config.count as number) ?? 5}
            onChange={(e) => updateConfig('count', parseInt(e.target.value) || 5)}
          />
        </FieldGroup>
      ) : (
        <FieldGroup label="While Condition">
          <Input
            className="h-8 text-xs font-mono"
            value={(config.condition as string) ?? ''}
            onChange={(e) => updateConfig('condition', e.target.value)}
            placeholder="output.hasMore === true"
          />
        </FieldGroup>
      )}
      <FieldGroup label="Batch Size">
        <Input
          className="h-8 text-xs"
          type="number"
          min={1}
          max={100}
          value={(config.batchSize as number) ?? 1}
          onChange={(e) => updateConfig('batchSize', parseInt(e.target.value) || 1)}
        />
      </FieldGroup>
      <FieldGroup label="Max Iterations">
        <Input
          className="h-8 text-xs"
          type="number"
          min={1}
          max={10000}
          value={(config.maxIterations as number) ?? 100}
          onChange={(e) => updateConfig('maxIterations', parseInt(e.target.value) || 100)}
        />
      </FieldGroup>
      <div className="flex items-center justify-between px-1">
        <Label className="text-xs">Run in Parallel</Label>
        <Switch
          checked={(config.parallel as boolean) ?? false}
          onCheckedChange={(v) => updateConfig('parallel', v)}
        />
      </div>
    </>
  );

  const renderDelayProps = () => (
    <>
      <FieldGroup label="Duration">
        <div className="flex gap-2">
          <Input
            className="h-8 text-xs flex-1"
            type="number"
            min={1}
            value={(config.duration as number) ?? 5}
            onChange={(e) => updateConfig('duration', parseInt(e.target.value) || 5)}
          />
          <Select
            value={(config.unit as string) ?? 'minutes'}
            onValueChange={(v) => updateConfig('unit', v)}
          >
            <SelectTrigger className="h-8 text-xs w-[100px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="seconds">Seconds</SelectItem>
              <SelectItem value="minutes">Minutes</SelectItem>
              <SelectItem value="hours">Hours</SelectItem>
              <SelectItem value="days">Days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </FieldGroup>
    </>
  );

  const renderWebhookProps = () => {
    const auth = (config.auth as string) ?? 'none';
    const bodyType = (config.bodyType as string) ?? 'json';
    return (
      <>
        <FieldGroup label="URL">
          <ExpressionField
            value={(config.url as string) ?? ''}
            onChange={(v) => updateConfig('url', v)}
            placeholder="https://api.example.com/webhook"
          />
        </FieldGroup>
        <FieldGroup label="Method">
          <Select
            value={(config.method as string) ?? 'POST'}
            onValueChange={(v) => updateConfig('method', v)}
          >
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="GET">GET</SelectItem>
              <SelectItem value="POST">POST</SelectItem>
              <SelectItem value="PUT">PUT</SelectItem>
              <SelectItem value="PATCH">PATCH</SelectItem>
              <SelectItem value="DELETE">DELETE</SelectItem>
              <SelectItem value="HEAD">HEAD</SelectItem>
            </SelectContent>
          </Select>
        </FieldGroup>

        {/* Authentication — was missing entirely; n8n's HTTP Request
            node treats this as the headline feature */}
        <FieldGroup label="Authentication">
          <Select
            value={auth}
            onValueChange={(v) => updateConfig('auth', v)}
          >
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              <SelectItem value="bearer">Bearer token</SelectItem>
              <SelectItem value="basic">Basic auth (user / password)</SelectItem>
              <SelectItem value="api_key">API key in header</SelectItem>
              <SelectItem value="custom_header">Custom header</SelectItem>
            </SelectContent>
          </Select>
        </FieldGroup>
        {auth === 'bearer' && (
          <FieldGroup label="Bearer token">
            <ExpressionField
              value={(config.authBearerToken as string) ?? ''}
              onChange={(v) => updateConfig('authBearerToken', v)}
              placeholder="{{ $vars.apiToken }}"
            />
            <p className="text-[9px] text-muted-foreground mt-1">Stored in plain config — for sensitive tokens, use a workflow variable.</p>
          </FieldGroup>
        )}
        {auth === 'basic' && (
          <>
            <FieldGroup label="Username">
              <Input
                className="h-8 text-xs"
                value={(config.authBasicUser as string) ?? ''}
                onChange={(e) => updateConfig('authBasicUser', e.target.value)}
              />
            </FieldGroup>
            <FieldGroup label="Password / Secret">
              <ExpressionField
                value={(config.authBasicPassword as string) ?? ''}
                onChange={(v) => updateConfig('authBasicPassword', v)}
                placeholder="{{ $vars.apiSecret }}"
              />
            </FieldGroup>
          </>
        )}
        {auth === 'api_key' && (
          <>
            <FieldGroup label="Header name">
              <Input
                className="h-8 text-xs font-mono"
                value={(config.authApiKeyHeader as string) ?? 'X-API-Key'}
                onChange={(e) => updateConfig('authApiKeyHeader', e.target.value)}
                placeholder="X-API-Key"
              />
            </FieldGroup>
            <FieldGroup label="API key value">
              <ExpressionField
                value={(config.authApiKeyValue as string) ?? ''}
                onChange={(v) => updateConfig('authApiKeyValue', v)}
                placeholder="{{ $vars.apiKey }}"
              />
            </FieldGroup>
          </>
        )}
        {auth === 'custom_header' && (
          <FieldGroup label="Custom auth header (full)">
            <Input
              className="h-8 text-xs font-mono"
              value={(config.authCustomHeader as string) ?? ''}
              onChange={(e) => updateConfig('authCustomHeader', e.target.value)}
              placeholder="Authorization: Token abc123"
            />
          </FieldGroup>
        )}

        {/* Query parameters as a key-value list (was missing) */}
        <FieldGroup label="Query parameters">
          <KeyValueList
            value={(config.queryParams as Array<{ key: string; value: string }>) ?? []}
            onChange={(next) => updateConfig('queryParams', next)}
            keyPlaceholder="param"
            valuePlaceholder="value"
            addLabel="Add param"
          />
        </FieldGroup>

        {/* Headers — kept as JSON for power users, plus key-value above
            for common cases (additive, runtime should merge) */}
        <FieldGroup label="Headers (JSON)">
          <Textarea
            className="text-xs font-mono min-h-[50px]"
            value={typeof config.headers === 'object' ? JSON.stringify(config.headers, null, 2) : (config.headers as string) ?? '{}'}
            onChange={(e) => {
              try {
                updateConfig('headers', JSON.parse(e.target.value));
              } catch {
                updateConfig('headers', e.target.value);
              }
            }}
            placeholder='{ "Content-Type": "application/json" }'
          />
        </FieldGroup>

        {/* Body type — JSON / form-data / urlencoded / raw / none */}
        <FieldGroup label="Body type">
          <Select
            value={bodyType}
            onValueChange={(v) => updateConfig('bodyType', v)}
          >
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              <SelectItem value="json">JSON</SelectItem>
              <SelectItem value="form_urlencoded">Form (urlencoded)</SelectItem>
              <SelectItem value="form_data">Form (multipart)</SelectItem>
              <SelectItem value="raw">Raw text</SelectItem>
            </SelectContent>
          </Select>
        </FieldGroup>

        {bodyType !== 'none' && (
          <FieldGroup label="Body">
            <ExpressionField
              value={(config.body as string) ?? ''}
              onChange={(v) => updateConfig('body', v)}
              placeholder={
                bodyType === 'json' ? '{"data": "Request body..."}'
                : bodyType === 'form_urlencoded' ? 'key1=value1&key2=value2'
                : bodyType === 'form_data' ? '(form fields configured below)'
                : 'Raw request body'
              }
              multiline
            />
          </FieldGroup>
        )}

        <FieldGroup label="Timeout (seconds)">
          <Input
            className="h-8 text-xs"
            type="number"
            min={1}
            max={600}
            value={(config.timeout as number) ?? 30}
            onChange={(e) => updateConfig('timeout', parseInt(e.target.value) || 30)}
          />
        </FieldGroup>
        <FieldGroup label="Follow redirects">
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-muted-foreground">Follow 30x responses to the new URL</span>
            <Switch
              checked={(config.followRedirects as boolean) ?? true}
              onCheckedChange={(v) => updateConfig('followRedirects', v)}
            />
          </div>
        </FieldGroup>
        <FieldGroup label="Retry count">
          <Input
            className="h-8 text-xs"
            type="number"
            min={0}
            max={10}
            value={(config.retries as number) ?? 3}
            onChange={(e) => updateConfig('retries', parseInt(e.target.value) || 0)}
          />
        </FieldGroup>
        <FieldGroup label="Response handling">
          <Select
            value={(config.responseMode as string) ?? 'json'}
            onValueChange={(v) => updateConfig('responseMode', v)}
          >
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="json">Parse JSON</SelectItem>
              <SelectItem value="text">Return as text</SelectItem>
              <SelectItem value="raw">Return as bytes (raw)</SelectItem>
              <SelectItem value="status">Status code only</SelectItem>
            </SelectContent>
          </Select>
        </FieldGroup>
      </>
    );
  };

  const renderNotificationProps = () => {
    const recipients = (config.recipients as string[]) ?? [];
    const ccList = (config.cc as string[]) ?? [];
    const channel = (config.channel as string) ?? 'email';
    const isEmailOrDraft = channel === 'email' || channel === 'email_draft';
    const isDraft = channel === 'email_draft';
    return (
      <>
        <FieldGroup label="Channel">
          <Select
            value={channel}
            onValueChange={(v) => updateConfig('channel', v)}
          >
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="email">Email — send immediately</SelectItem>
              <SelectItem value="email_draft">Email Draft — review before send</SelectItem>
              <SelectItem value="slack">Slack</SelectItem>
              <SelectItem value="teams">Microsoft Teams</SelectItem>
              <SelectItem value="sms">SMS</SelectItem>
              <SelectItem value="in_app">In-app notification</SelectItem>
            </SelectContent>
          </Select>
          {isDraft && (
            <p className="text-[9px] text-amber-600 dark:text-amber-400 mt-1">
              Draft mode posts the message to the Approvals queue. A human edits + approves before send.
            </p>
          )}
        </FieldGroup>

        {/* Email-specific fields (#F11/F12) */}
        {isEmailOrDraft && (
          <FieldGroup label="From mailbox">
            <Input
              className="h-7 text-xs"
              value={(config.fromMailbox as string) ?? ''}
              onChange={(e) => updateConfig('fromMailbox', e.target.value)}
              placeholder="support@argus.local"
            />
            <p className="text-[9px] text-muted-foreground mt-1">
              The connected mailbox the message is sent from. Configured in Connections.
            </p>
          </FieldGroup>
        )}

        <FieldGroup label="Recipients (To)">
          <div className="space-y-1">
            {recipients.map((r, i) => (
              <div key={i} className="flex gap-1">
                <Input
                  className="h-7 text-xs flex-1"
                  value={r}
                  onChange={(e) => {
                    const updated = [...recipients];
                    updated[i] = e.target.value;
                    updateConfig('recipients', updated);
                  }}
                  placeholder={isEmailOrDraft ? 'email@company.com' : '#channel or @user'}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 flex-shrink-0"
                  title="Remove recipient"
                  onClick={() => {
                    updateConfig('recipients', recipients.filter((_, j) => j !== i));
                  }}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              className="w-full h-7 text-xs"
              onClick={() => updateConfig('recipients', [...recipients, ''])}
            >
              <Plus className="h-3 w-3 mr-1" /> Add recipient
            </Button>
          </div>
        </FieldGroup>

        {isEmailOrDraft && (
          <>
            <FieldGroup label="CC">
              <div className="space-y-1">
                {ccList.map((c, i) => (
                  <div key={i} className="flex gap-1">
                    <Input
                      className="h-7 text-xs flex-1"
                      value={c}
                      onChange={(e) => {
                        const updated = [...ccList];
                        updated[i] = e.target.value;
                        updateConfig('cc', updated);
                      }}
                      placeholder="team-lead@company.com"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 flex-shrink-0"
                      title="Remove CC"
                      onClick={() => {
                        updateConfig('cc', ccList.filter((_, j) => j !== i));
                      }}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full h-7 text-xs"
                  onClick={() => updateConfig('cc', [...ccList, ''])}
                >
                  <Plus className="h-3 w-3 mr-1" /> Add CC
                </Button>
              </div>
            </FieldGroup>
            <FieldGroup label="Reply-To">
              <Input
                className="h-7 text-xs"
                value={(config.replyTo as string) ?? ''}
                onChange={(e) => updateConfig('replyTo', e.target.value)}
                placeholder="alerts@argus.local"
              />
            </FieldGroup>
          </>
        )}

        <FieldGroup label="Subject">
          <ExpressionField
            value={(config.subject as string) ?? ''}
            onChange={v => updateConfig('subject', v)}
            placeholder={isEmailOrDraft ? 'Subject line...' : 'Notification subject...'}
          />
        </FieldGroup>

        <FieldGroup label={isEmailOrDraft ? 'Body (HTML or plain text)' : 'Message'}>
          <ExpressionField
            value={(config.message as string) ?? ''}
            onChange={v => updateConfig('message', v)}
            placeholder={isEmailOrDraft
              ? 'Email body. Use {{ $node[\'Agent\'].json.draft }} to insert agent output.'
              : 'Notification message body...'}
            multiline
          />
        </FieldGroup>

        {isDraft && (
          <FieldGroup label="Approval policy">
            <Select
              value={(config.approvalLevel as string) ?? 'single'}
              onValueChange={(v) => updateConfig('approvalLevel', v)}
            >
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="single">Any reviewer</SelectItem>
                <SelectItem value="supervisor">Manager / supervisor</SelectItem>
                <SelectItem value="two_person">Two-person rule</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[9px] text-muted-foreground mt-1">
              Who must approve the draft before it's sent.
            </p>
          </FieldGroup>
        )}
      </>
    );
  };

  // ── New node type property renderers ──────────────────────────────────

  const renderLlmGenerateProps = () => (
    <>
      <FieldGroup label="Prompt Template">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mb-1 h-7 w-full text-[10px] gap-1"
          onClick={() => setPromptApplyTarget('llm')}
        >
          <Sparkles className="h-3 w-3" />
          Apply Studio prompt
        </Button>
        <ExpressionField
          value={(config.promptTemplate as string) ?? ''}
          onChange={v => updateConfig('promptTemplate', v)}
          placeholder="Enter the prompt template. Use Insert button for dynamic values."
          multiline
        />
        {typeof config.promptTemplateTitle === 'string' && config.promptTemplateTitle.length > 0 && (
          <p className="text-[9px] text-muted-foreground mt-1">
            Applied from <span className="font-medium">{config.promptTemplateTitle}</span>
            {typeof config.promptTemplateCommand === 'string' && config.promptTemplateCommand.length > 0 ? <span className="font-mono"> /{config.promptTemplateCommand}</span> : null}
          </p>
        )}
      </FieldGroup>
      <FieldGroup label="Model">
        <Select value={(config.model as string) ?? 'default'} onValueChange={v => updateConfig('model', v)}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="default">Default (Qwen 72B AWQ)</SelectItem>
            <SelectItem value="fast">Fast (Llama 3.2 3B)</SelectItem>
            <SelectItem value="code">Code (Qwen Coder 32B)</SelectItem>
            <SelectItem value="precise">Precise (72B, temp 0.1)</SelectItem>
          </SelectContent>
        </Select>
      </FieldGroup>
      <FieldGroup label={`Temperature: ${((config.temperature as number) ?? 0.7).toFixed(1)}`}>
        <Slider min={0} max={1} step={0.1} value={[(config.temperature as number) ?? 0.7]} onValueChange={([v]) => updateConfig('temperature', v)} />
      </FieldGroup>
      <FieldGroup label={`Max Tokens: ${(config.maxTokens as number) ?? 2048}`}>
        <Slider min={256} max={8192} step={256} value={[(config.maxTokens as number) ?? 2048]} onValueChange={([v]) => updateConfig('maxTokens', v)} />
      </FieldGroup>
      <FieldGroup label="Output Format">
        <Select value={(config.outputFormat as string) ?? 'text'} onValueChange={v => updateConfig('outputFormat', v)}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="text">Free Text</SelectItem>
            <SelectItem value="json">JSON (structured)</SelectItem>
            <SelectItem value="markdown">Markdown</SelectItem>
          </SelectContent>
        </Select>
      </FieldGroup>
    </>
  );

  const renderRagSearchProps = () => {
    const ragConfig = normalizeRagSearchNodeConfig(config);
    const topK = ragConfig.top_k as number;

    return (
      <>
        <FieldGroup label="Query Template">
          <ExpressionField
            value={(ragConfig.query_template as string) ?? ''}
            onChange={v => updateConfig('query_template', v)}
          placeholder="Search query — use Insert for dynamic values"
            multiline
          />
        </FieldGroup>
        <FieldGroup label="Collection">
          <Input
            className="h-8 text-xs font-mono"
            value={(ragConfig.collection as string) ?? RAG_SEARCH_DEFAULT_CONFIG.collection}
            onChange={(e) => updateConfig('collection', e.target.value)}
            placeholder={RAG_SEARCH_DEFAULT_CONFIG.collection}
          />
        </FieldGroup>
        <FieldGroup label={`Top K: ${topK}`}>
          <Slider
            min={1}
            max={50}
            step={1}
            value={[topK]}
            onValueChange={([v]) => updateConfig('top_k', v)}
          />
        </FieldGroup>
        <FieldGroup label="Filters">
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <span className="text-[10px] text-muted-foreground">Classification</span>
              <Switch
                checked
                disabled
              />
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-[10px] text-muted-foreground">Document ACL</span>
              <Switch
                checked
                disabled
              />
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-[10px] text-muted-foreground">Reranker</span>
              <Switch
                checked={ragConfig.apply_reranker as boolean}
                onCheckedChange={v => updateConfig('apply_reranker', v)}
              />
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-[10px] text-muted-foreground">CRAG Gate</span>
              <Switch
                checked={ragConfig.apply_crag_gate as boolean}
                onCheckedChange={v => updateConfig('apply_crag_gate', v)}
              />
            </div>
          </div>
        </FieldGroup>
      </>
    );
  };

  const renderSetVariableProps = () => {
    type Assign = {
      name: string;
      type: 'string' | 'number' | 'boolean' | 'json';
      expression: string;
      fallback?: string;
    };
    // Migrate legacy single-variable config -> assignments array
    const assignments: Assign[] =
      (config.assignments as Assign[] | undefined) ??
      (config.variableName !== undefined
        ? [{
            name: (config.variableName as string) ?? '',
            type: 'string',
            expression: (config.expression as string) ?? '',
            fallback: (config.fallback as string) ?? '',
          }]
        : [{ name: '', type: 'string', expression: '' }]);

    const updateAssign = (i: number, next: Partial<Assign>) =>
      updateConfig('assignments', assignments.map((a, j) => (j === i ? { ...a, ...next } : a)));
    const removeAssign = (i: number) =>
      updateConfig('assignments', assignments.filter((_, j) => j !== i));
    const addAssign = () =>
      updateConfig('assignments', [...assignments, { name: '', type: 'string' as const, expression: '' }]);

    return (
      <>
        <p className="text-[10px] text-muted-foreground italic px-1">
          Set or transform one or more workflow variables. Each variable becomes available downstream as <code className="text-[9px]">{'{{ $vars.<name> }}'}</code>.
        </p>
        <div className="space-y-3">
          {assignments.map((a, i) => (
            <div key={i} className="border rounded-md p-2 space-y-1.5 bg-muted/20">
              <div className="flex items-center justify-between">
                <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Variable {i + 1}
                </span>
                {assignments.length > 1 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 -my-1"
                    title="Remove variable"
                    onClick={() => removeAssign(i)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
              <FieldGroup label="Name">
                <Input
                  className="h-7 text-xs font-mono"
                  value={a.name}
                  onChange={(e) => updateAssign(i, { name: e.target.value })}
                  placeholder="processed_data"
                />
              </FieldGroup>
              <FieldGroup label="Type">
                <Select
                  value={a.type}
                  onValueChange={(v) => updateAssign(i, { type: v as Assign['type'] })}
                >
                  <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="string">String</SelectItem>
                    <SelectItem value="number">Number</SelectItem>
                    <SelectItem value="boolean">Boolean</SelectItem>
                    <SelectItem value="json">JSON object/array</SelectItem>
                  </SelectContent>
                </Select>
              </FieldGroup>
              <FieldGroup label="Value (literal or expression)">
                <ExpressionField
                  value={a.expression}
                  onChange={(v) => updateAssign(i, { expression: v })}
                  placeholder={a.type === 'number' ? '42' : a.type === 'boolean' ? 'true' : a.type === 'json' ? '{ "k": "v" }' : '{{ $input.json.value }}'}
                  multiline={a.type === 'json'}
                />
              </FieldGroup>
              <FieldGroup label="Fallback (if expression errors)">
                <Input
                  className="h-7 text-xs font-mono"
                  value={a.fallback ?? ''}
                  onChange={(e) => updateAssign(i, { fallback: e.target.value })}
                  placeholder="Optional"
                />
              </FieldGroup>
            </div>
          ))}
          <Button variant="outline" size="sm" className="w-full h-7 text-xs" onClick={addAssign}>
            <Plus className="h-3 w-3 mr-1" /> Add variable
          </Button>
        </div>
      </>
    );
  };

  const renderMergeProps = () => (
    <>
      <FieldGroup label="Merge Strategy">
        <Select value={(config.strategy as string) ?? 'append'} onValueChange={v => updateConfig('strategy', v)}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="append">Append (combine all items)</SelectItem>
            <SelectItem value="join">Join by Key (match field)</SelectItem>
            <SelectItem value="zip">Zip (pair by position)</SelectItem>
            <SelectItem value="first">First Non-Empty (use first branch with data)</SelectItem>
          </SelectContent>
        </Select>
      </FieldGroup>
      {(config.strategy as string) === 'join' && (
        <FieldGroup label="Join Key">
          <Input className="h-8 text-xs font-mono" value={(config.joinKey as string) ?? ''} onChange={e => updateConfig('joinKey', e.target.value)} placeholder="e.g., id" />
        </FieldGroup>
      )}
      <FieldGroup label="Wait for All Inputs">
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-muted-foreground">Wait for all branches before proceeding</span>
          <Switch checked={(config.waitAll as boolean) ?? true} onCheckedChange={v => updateConfig('waitAll', v)} />
        </div>
      </FieldGroup>
    </>
  );

  const renderCodeProps = () => (
    <>
      <FieldGroup label="Language">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Code className="h-3.5 w-3.5" />
          <span>JavaScript — runs in an isolated Node <span className="font-mono">vm</span> sandbox</span>
        </div>
      </FieldGroup>
      <FieldGroup label="Code">
        <Textarea className="text-xs font-mono min-h-[120px]" value={(config.code as string) ?? ''} onChange={e => updateConfig('code', e.target.value)} placeholder={`// input = data from the previous node\n// ctx.params = this node's params; ctx.httpClient.fetch(url) = allowlisted HTTP (await it)\n// Return the value passed to the next node:\nreturn (input.items ?? []).filter(i => i.status === 'ready');`} />
        <p className="text-[9px] text-muted-foreground mt-1">Signature <span className="font-mono">(input, ctx)</span>. Available: <span className="font-mono">input</span> (previous step output), <span className="font-mono">ctx.params</span>, <span className="font-mono">ctx.httpClient.fetch(url)</span> (allowlisted HTTP). No <span className="font-mono">globalThis.fetch</span> / <span className="font-mono">import()</span>.</p>
      </FieldGroup>
      <FieldGroup label="Timeout (seconds)">
        <Input className="h-8 text-xs" type="number" min={1} max={300} value={(config.timeout as number) ?? 30} onChange={e => updateConfig('timeout', parseInt(e.target.value) || 30)} />
      </FieldGroup>
    </>
  );

  const renderSubWorkflowProps = () => <SubWorkflowProps config={config} updateConfig={updateConfig} />;

  // ── Extended node property renderers (15 new nodes) ──────────────────

  const renderSwitchProps = () => (
    <>
      <FieldGroup label="Switch Field">
        <Input className="h-8 text-xs font-mono" value={(config.switchField as string) ?? ''} onChange={e => updateConfig('switchField', e.target.value)} placeholder="e.g., department" />
      </FieldGroup>
      <FieldGroup label="Cases">
        {((config.cases as string[]) ?? ['case1']).map((c, i) => (
          <div key={i} className="flex items-center gap-1 mb-1">
            <Input className="h-7 text-xs flex-1" value={c} onChange={e => { const cases = [...((config.cases as string[]) ?? [])]; cases[i] = e.target.value; updateConfig('cases', cases); }} />
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { const cases = ((config.cases as string[]) ?? []).filter((_, j) => j !== i); updateConfig('cases', cases); }} aria-label="Remove case" title="Remove case"><X className="h-3 w-3" /></Button>
          </div>
        ))}
        <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => updateConfig('cases', [...((config.cases as string[]) ?? []), `case${((config.cases as string[]) ?? []).length + 1}`])}><Plus className="h-3 w-3" /> Add Case</Button>
      </FieldGroup>
      <FieldGroup label="Default Output">
        <div className="flex items-center justify-between"><span className="text-[10px] text-muted-foreground">Include default (no match) output</span><Switch checked={(config.hasDefault as boolean) ?? true} onCheckedChange={v => updateConfig('hasDefault', v)} /></div>
      </FieldGroup>
    </>
  );

  const renderFilterProps = () => (
    <>
      <FieldGroup label="Filter Field">
        <ExpressionField
          value={(config.field as string) ?? ''}
          onChange={v => updateConfig('field', v)}
          placeholder="e.g., status"
        />
      </FieldGroup>
      <FieldGroup label="Operator">
        <Select value={(config.operator as string) ?? 'eq'} onValueChange={v => updateConfig('operator', v)}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="eq">Equals</SelectItem><SelectItem value="neq">Not Equals</SelectItem>
            <SelectItem value="contains">Contains</SelectItem><SelectItem value="gt">Greater Than</SelectItem>
            <SelectItem value="lt">Less Than</SelectItem><SelectItem value="exists">Exists</SelectItem>
            <SelectItem value="regex">Regex Match</SelectItem>
          </SelectContent>
        </Select>
      </FieldGroup>
      <FieldGroup label="Value">
        <ExpressionField
          value={(config.value as string) ?? ''}
          onChange={v => updateConfig('value', v)}
          placeholder="Value to compare"
        />
      </FieldGroup>
    </>
  );

  const renderAggregateProps = () => (
    <>
      <FieldGroup label="Operation">
        <Select value={(config.operation as string) ?? 'count'} onValueChange={v => updateConfig('operation', v)}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="count">Count</SelectItem><SelectItem value="sum">Sum</SelectItem>
            <SelectItem value="average">Average</SelectItem><SelectItem value="min">Min</SelectItem>
            <SelectItem value="max">Max</SelectItem><SelectItem value="first">First</SelectItem>
            <SelectItem value="last">Last</SelectItem><SelectItem value="concat">Concatenate</SelectItem>
          </SelectContent>
        </Select>
      </FieldGroup>
      <FieldGroup label="Field (for sum/avg/min/max)">
        <Input className="h-8 text-xs font-mono" value={(config.aggregateField as string) ?? ''} onChange={e => updateConfig('aggregateField', e.target.value)} placeholder="e.g., riskScore" />
      </FieldGroup>
      <FieldGroup label="Group By (optional)">
        <Input className="h-8 text-xs font-mono" value={(config.groupBy as string) ?? ''} onChange={e => updateConfig('groupBy', e.target.value)} placeholder="e.g., department" />
      </FieldGroup>
    </>
  );

  const renderSplitOutProps = () => (
    <>
      <FieldGroup label="Array Field">
        <Input className="h-8 text-xs font-mono" value={(config.arrayField as string) ?? ''} onChange={e => updateConfig('arrayField', e.target.value)} placeholder="e.g., items" />
        <p className="text-[9px] text-muted-foreground mt-1">Path to the array field to split into individual items</p>
      </FieldGroup>
      <FieldGroup label="Include Parent Fields">
        <div className="flex items-center justify-between"><span className="text-[10px] text-muted-foreground">Include non-array fields in each output item</span><Switch checked={(config.includeParent as boolean) ?? true} onCheckedChange={v => updateConfig('includeParent', v)} /></div>
      </FieldGroup>
    </>
  );

  const renderSortProps = () => {
    type Key = { field: string; direction: 'asc' | 'desc'; type: 'string' | 'number' | 'date' };
    // Migrate legacy single-field config -> keys array
    const keys: Key[] =
      (config.keys as Key[] | undefined) ??
      (config.sortField !== undefined
        ? [{
            field: (config.sortField as string) ?? '',
            direction: (config.direction as 'asc' | 'desc') ?? 'asc',
            type: 'string' as const,
          }]
        : [{ field: '', direction: 'asc' as const, type: 'string' as const }]);

    const updateKey = (i: number, next: Partial<Key>) =>
      updateConfig('keys', keys.map((k, j) => (j === i ? { ...k, ...next } : k)));
    const removeKey = (i: number) =>
      updateConfig('keys', keys.filter((_, j) => j !== i));
    const addKey = () =>
      updateConfig('keys', [...keys, { field: '', direction: 'asc' as const, type: 'string' as const }]);

    return (
      <>
        <div className="space-y-3">
          {keys.map((k, i) => (
            <div key={i} className="border rounded-md p-2 space-y-1.5 bg-muted/20">
              <div className="flex items-center justify-between">
                <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Sort key {i + 1}{i === 0 ? ' (primary)' : ''}
                </span>
                {keys.length > 1 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 -my-1"
                    title="Remove key"
                    onClick={() => removeKey(i)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
              <FieldGroup label="Field">
                <Input
                  className="h-7 text-xs font-mono"
                  value={k.field}
                  onChange={(e) => updateKey(i, { field: e.target.value })}
                  placeholder="priority"
                />
              </FieldGroup>
              <FieldGroup label="Type">
                <Select value={k.type} onValueChange={(v) => updateKey(i, { type: v as Key['type'] })}>
                  <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="string">String (lexicographic)</SelectItem>
                    <SelectItem value="number">Number</SelectItem>
                    <SelectItem value="date">Date</SelectItem>
                  </SelectContent>
                </Select>
              </FieldGroup>
              <FieldGroup label="Direction">
                <Select value={k.direction} onValueChange={(v) => updateKey(i, { direction: v as Key['direction'] })}>
                  <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="asc">Ascending</SelectItem>
                    <SelectItem value="desc">Descending</SelectItem>
                  </SelectContent>
                </Select>
              </FieldGroup>
            </div>
          ))}
          <Button variant="outline" size="sm" className="w-full h-7 text-xs" onClick={addKey}>
            <Plus className="h-3 w-3 mr-1" /> Add sort key
          </Button>
        </div>
        <FieldGroup label="Nulls">
          <Select value={(config.nulls as string) ?? 'last'} onValueChange={(v) => updateConfig('nulls', v)}>
            <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="last">Nulls last</SelectItem>
              <SelectItem value="first">Nulls first</SelectItem>
            </SelectContent>
          </Select>
        </FieldGroup>
      </>
    );
  };

  const renderLimitProps = () => (
    <>
      <FieldGroup label="Count">
        <Input className="h-8 text-xs" type="number" min={1} max={10000} value={(config.count as number) ?? 10} onChange={e => updateConfig('count', parseInt(e.target.value) || 10)} />
      </FieldGroup>
      <FieldGroup label="From">
        <Select value={(config.from as string) ?? 'start'} onValueChange={v => updateConfig('from', v)}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="start">First N items</SelectItem><SelectItem value="end">Last N items</SelectItem></SelectContent>
        </Select>
      </FieldGroup>
    </>
  );

  const renderRemoveDuplicatesProps = () => (
    <>
      <FieldGroup label="Deduplicate By Field">
        <Input className="h-8 text-xs font-mono" value={(config.dedupeField as string) ?? ''} onChange={e => updateConfig('dedupeField', e.target.value)} placeholder="e.g., id" />
      </FieldGroup>
      <FieldGroup label="Keep">
        <Select value={(config.keep as string) ?? 'first'} onValueChange={v => updateConfig('keep', v)}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="first">First occurrence</SelectItem><SelectItem value="last">Last occurrence</SelectItem></SelectContent>
        </Select>
      </FieldGroup>
    </>
  );

  const renderErrorHandlerProps = () => <ErrorHandlerProps config={config} updateConfig={updateConfig} />;

  const renderStopProps = () => (
    <>
      <FieldGroup label="Stop Mode">
        <Select value={(config.mode as string) ?? 'success'} onValueChange={v => updateConfig('mode', v)}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="success">Success (complete workflow)</SelectItem><SelectItem value="error">Error (fail workflow with message)</SelectItem></SelectContent>
        </Select>
      </FieldGroup>
      <FieldGroup label="Message">
        <Textarea className="text-xs min-h-[40px]" value={(config.message as string) ?? ''} onChange={e => updateConfig('message', e.target.value)} placeholder="Completion/error message..." />
      </FieldGroup>
    </>
  );

  const renderWaitForEventProps = () => {
    const waitType = (config.waitType as string) ?? 'webhook';
    const onTimeout = (config.onTimeout as string) ?? 'error';
    return (
      <>
        <FieldGroup label="Wait for">
          <Select value={waitType} onValueChange={(v) => updateConfig('waitType', v)}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="webhook">Webhook callback (resume URL)</SelectItem>
              <SelectItem value="event">System event</SelectItem>
              <SelectItem value="approval">Approval response</SelectItem>
              <SelectItem value="external">External signal (named)</SelectItem>
            </SelectContent>
          </Select>
        </FieldGroup>

        {waitType === 'webhook' && (
          <FieldGroup
            label="Resume URL preview"
            help={<span className="text-[9px] text-muted-foreground">Generated at runtime. Send a POST here from the external system to resume.</span>}
          >
            <Input
              className="h-8 text-[10px] font-mono"
              value="/api/v1/automate-waits/<run-id>/<wait-id>"
              readOnly
            />
          </FieldGroup>
        )}
        {waitType === 'event' && (
          <>
            <FieldGroup label="Event name to match">
              <Input
                className="h-8 text-xs font-mono"
                value={(config.eventName as string) ?? ''}
                onChange={(e) => updateConfig('eventName', e.target.value)}
                placeholder="plant.shift.handover"
              />
            </FieldGroup>
            <FieldGroup
              label="Match expression (optional)"
              help={<span className="text-[9px] text-muted-foreground">Boolean expression evaluated against each event's payload. Resume on first match.</span>}
            >
              <ExpressionField
                value={(config.matchExpr as string) ?? ''}
                onChange={(v) => updateConfig('matchExpr', v)}
                placeholder='{{ $event.json.assetId === $node["Trigger"].json.assetId }}'
                multiline
              />
            </FieldGroup>
          </>
        )}
        {waitType === 'external' && (
          <FieldGroup label="Signal name">
            <Input
              className="h-8 text-xs font-mono"
              value={(config.signalName as string) ?? ''}
              onChange={(e) => updateConfig('signalName', e.target.value)}
              placeholder="cmms.work-order-completed"
            />
          </FieldGroup>
        )}

        <FieldGroup label="Timeout (minutes)">
          <Input
            className="h-8 text-xs"
            type="number"
            min={1}
            max={43200}
            value={(config.timeoutMinutes as number) ?? 60}
            onChange={(e) => updateConfig('timeoutMinutes', parseInt(e.target.value) || 60)}
          />
        </FieldGroup>
        <FieldGroup label="On timeout">
          <Select value={onTimeout} onValueChange={(v) => updateConfig('onTimeout', v)}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="error">Fail workflow</SelectItem>
              <SelectItem value="continue">Continue with null payload</SelectItem>
              <SelectItem value="default">Use default value</SelectItem>
            </SelectContent>
          </Select>
        </FieldGroup>
        {onTimeout === 'default' && (
          <FieldGroup label="Default value (JSON)">
            <Textarea
              className="text-xs font-mono min-h-[50px]"
              value={(config.defaultValue as string) ?? ''}
              onChange={(e) => updateConfig('defaultValue', e.target.value)}
              placeholder='{"timedOut": true}'
            />
          </FieldGroup>
        )}
      </>
    );
  };

  // NOTE: render functions for the five UI-only node kinds
  // (http_response / date_time / template / parse_generate /
  // workspace_handoff) were removed in fix/automate-carryover-majors
  // (round-7 NEW-MAJOR-3). They had no backend support, so the
  // canvas could surface them but a save would silently drop the
  // node. Restore on both sides (this file + the panel switch above
  // + workflow-nodes.tsx + the backend WORKFLOW_NODE_KINDS enum)
  // when the kinds ship as real product features.

  // node-quality #04 — Emit Event had no property panel; config was
  // unreachable. Keys match execEmitEvent (event_name / payload_template /
  // dedupe_key_template / expires_in_seconds).
  const renderEmitEventProps = () => (
    <>
      <FieldGroup label="Event Name">
        <Input
          className="h-8 text-xs"
          value={(config.event_name as string) ?? ''}
          onChange={(e) => updateConfig('event_name', e.target.value)}
          placeholder="e.g. order.created"
        />
      </FieldGroup>
      <FieldGroup label="Payload (JSON)">
        <Textarea
          className="text-xs font-mono min-h-[100px]"
          value={
            typeof config.payload_template === 'object'
              ? JSON.stringify(config.payload_template, null, 2)
              : (config.payload_template as string) ?? '{}'
          }
          onChange={(e) => {
            try {
              updateConfig('payload_template', JSON.parse(e.target.value));
            } catch {
              updateConfig('payload_template', e.target.value);
            }
          }}
          placeholder={'{\n  "id": "{{ $json.id }}"\n}'}
        />
      </FieldGroup>
      <FieldGroup label="Dedupe Key (optional)">
        <Input
          className="h-8 text-xs"
          value={(config.dedupe_key_template as string) ?? ''}
          onChange={(e) => updateConfig('dedupe_key_template', e.target.value)}
          placeholder="e.g. {{ $json.id }}"
        />
      </FieldGroup>
      <FieldGroup label="Expires In (seconds, optional)">
        <Input
          className="h-8 text-xs"
          type="number"
          min={0}
          value={(config.expires_in_seconds as number) ?? ''}
          onChange={(e) =>
            updateConfig('expires_in_seconds', e.target.value ? parseInt(e.target.value) : undefined)
          }
        />
      </FieldGroup>
    </>
  );

  // node-quality #13 — Wait for Condition had no property panel, so the
  // required `field` could never be set and the node always threw. Keys match
  // execWaitForCondition.
  const renderWaitForConditionProps = () => (
    <>
      <FieldGroup label="Field">
        <Input
          className="h-8 text-xs"
          value={(config.field as string) ?? ''}
          onChange={(e) => updateConfig('field', e.target.value)}
          placeholder="e.g. status"
        />
      </FieldGroup>
      <FieldGroup label="Operator">
        <Select value={(config.operator as string) ?? 'eq'} onValueChange={(v) => updateConfig('operator', v)}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="eq">equals</SelectItem>
            <SelectItem value="neq">not equals</SelectItem>
            <SelectItem value="gt">greater than</SelectItem>
            <SelectItem value="lt">less than</SelectItem>
            <SelectItem value="gte">greater or equal</SelectItem>
            <SelectItem value="lte">less or equal</SelectItem>
            <SelectItem value="contains">contains</SelectItem>
            <SelectItem value="exists">exists</SelectItem>
          </SelectContent>
        </Select>
      </FieldGroup>
      <FieldGroup label="Value">
        <Input
          className="h-8 text-xs"
          value={(config.value as string) ?? ''}
          onChange={(e) => updateConfig('value', e.target.value)}
        />
      </FieldGroup>
      <FieldGroup label="Value Type">
        <Select value={(config.valueType as string) ?? 'string'} onValueChange={(v) => updateConfig('valueType', v)}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="string">String</SelectItem>
            <SelectItem value="number">Number</SelectItem>
            <SelectItem value="boolean">Boolean</SelectItem>
          </SelectContent>
        </Select>
      </FieldGroup>
      <FieldGroup label="Poll Interval (ms)">
        <Input
          className="h-8 text-xs"
          type="number"
          min={1000}
          max={60000}
          value={(config.pollIntervalMs as number) ?? 5000}
          onChange={(e) => updateConfig('pollIntervalMs', parseInt(e.target.value) || 5000)}
        />
      </FieldGroup>
      <FieldGroup label="Timeout (ms)">
        <Input
          className="h-8 text-xs"
          type="number"
          min={1000}
          max={300000}
          value={(config.timeoutMs as number) ?? 30000}
          onChange={(e) => updateConfig('timeoutMs', parseInt(e.target.value) || 30000)}
        />
      </FieldGroup>
    </>
  );

  // node-quality #41 — Transform had no property panel, so config.template
  // could never be set and the node always threw. Key matches execTransform.
  const renderTransformProps = () => (
    <>
      <FieldGroup label="Per-item Template (JSON)">
        <Textarea
          className="text-xs font-mono min-h-[120px]"
          value={
            typeof config.template === 'object'
              ? JSON.stringify(config.template, null, 2)
              : (config.template as string) ?? '{}'
          }
          onChange={(e) => {
            try {
              updateConfig('template', JSON.parse(e.target.value));
            } catch {
              updateConfig('template', e.target.value);
            }
          }}
          placeholder={'{\n  "label": "Asset #{{id}}: {{name}}"\n}'}
        />
      </FieldGroup>
      <p className="text-[11px] text-muted-foreground px-1">
        Maps each upstream item to a new object. Use {'{{field}}'} to reference the item&apos;s fields.
      </p>
    </>
  );

  // ── Type switch ───────────────────────────────────────────────────────

  const renderTypeProps = () => {
    switch (nodeType) {
      case 'trigger': return renderTriggerProps();
      case 'agent': return renderAgentProps();
      case 'mcp_tool': return renderToolProps();
      case 'condition': return renderConditionProps();
      case 'ai_router': return renderAiRouterProps();
      case 'approval': return renderApprovalProps();
      case 'loop': return renderLoopProps();
      case 'delay': return renderDelayProps();
      case 'webhook': return renderWebhookProps();
      case 'notification': return renderNotificationProps();
      case 'llm_generate': return renderLlmGenerateProps();
      case 'rag_search': return renderRagSearchProps();
      case 'set_variable': return renderSetVariableProps();
      case 'merge': return renderMergeProps();
      case 'code': return renderCodeProps();
      case 'sub_workflow': return renderSubWorkflowProps();
      case 'switch': return renderSwitchProps();
      case 'filter': return renderFilterProps();
      case 'aggregate': return renderAggregateProps();
      case 'split_out': return renderSplitOutProps();
      case 'sort': return renderSortProps();
      case 'limit': return renderLimitProps();
      case 'remove_duplicates': return renderRemoveDuplicatesProps();
      case 'error_handler': return renderErrorHandlerProps();
      case 'stop': return renderStopProps();
      case 'wait_for_event': return renderWaitForEventProps();
      case 'wait_for_condition': return renderWaitForConditionProps();
      case 'transform': return renderTransformProps();
      case 'emit_event': return renderEmitEventProps();
      // Five UI-only node-type cases (http_response / date_time /
      // template / parse_generate / workspace_handoff) were removed in
      // fix/automate-carryover-majors — they had no backend support
      // (round-7 NEW-MAJOR-3). Re-add on both sides together.
      default: return null;
    }
  };

  return (
    <>
    <div className="w-[320px] flex flex-col border-l bg-muted/30 h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b">
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-md bg-muted/80">
            <Icon className={cn('h-3.5 w-3.5', cfg.color)} />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold truncate">{cfg.label}</p>
            <p className="text-[10px] text-muted-foreground">Node Properties</p>
          </div>
        </div>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose} title="Close panel">
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Scrollable content */}
      <ScrollArea className="flex-1">
        <div className="p-3 space-y-3">
          {/* Common: label */}
          <FieldGroup label="Label">
            <Input
              className="h-8 text-xs"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </FieldGroup>

          <Separator />

          {/* Type-specific props */}
          {renderTypeProps()}
        </div>
      </ScrollArea>

      {/* Footer */}
      <div className="flex gap-2 px-3 py-2.5 border-t">
        <Button
          variant="destructive"
          size="sm"
          className="h-8 text-xs gap-1"
          onClick={() => onDelete(nodeId)}
          title="Delete this node"
        >
          <Trash2 className="h-3 w-3" /> Delete
        </Button>
        <Button
          size="sm"
          className="h-8 text-xs flex-1"
          onClick={handleApply}
        >
          Apply
        </Button>
      </div>
    </div>
    <PromptApplyDialog
      open={promptApplyTarget !== null}
      onOpenChange={(open) => {
        if (!open) setPromptApplyTarget(null);
      }}
      applyLabel={promptApplyTarget === 'llm' ? 'Use as prompt template' : 'Use as instructions'}
      onApply={(text, source) => {
        if (promptApplyTarget === 'llm') {
          setConfig((prev) => ({
            ...prev,
            promptTemplate: text,
            promptTemplateId: source.id,
            promptTemplateCommand: source.command,
            promptTemplateTitle: source.title,
          }));
        } else {
          setConfig((prev) => ({
            ...prev,
            prompt: text,
            promptTemplateId: source.id,
            promptTemplateCommand: source.command,
            promptTemplateTitle: source.title,
          }));
        }
      }}
    />
    </>
  );
}

// ── FieldGroup helper ────────────────────────────────────────────────────

function FieldGroup({
  label,
  children,
  help,
}: {
  label: string;
  children: React.ReactNode;
  help?: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
      {help}
    </div>
  );
}

// ── Reusable key/value list editor — used by webhook query params,
// http_response headers, future header forms.

function KeyValueList({
  value,
  onChange,
  keyPlaceholder = 'key',
  valuePlaceholder = 'value',
  addLabel = 'Add row',
}: {
  value: Array<{ key: string; value: string }>;
  onChange: (next: Array<{ key: string; value: string }>) => void;
  keyPlaceholder?: string;
  valuePlaceholder?: string;
  addLabel?: string;
}) {
  return (
    <div className="space-y-1">
      {value.map((row, i) => (
        <div key={i} className="flex gap-1">
          <Input
            className="h-7 text-xs flex-1 font-mono"
            value={row.key}
            onChange={(e) => {
              const next = [...value];
              next[i] = { ...row, key: e.target.value };
              onChange(next);
            }}
            placeholder={keyPlaceholder}
          />
          <Input
            className="h-7 text-xs flex-1 font-mono"
            value={row.value}
            onChange={(e) => {
              const next = [...value];
              next[i] = { ...row, value: e.target.value };
              onChange(next);
            }}
            placeholder={valuePlaceholder}
          />
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 flex-shrink-0"
            title="Remove row"
            onClick={() => onChange(value.filter((_, j) => j !== i))}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      ))}
      <Button
        variant="outline"
        size="sm"
        className="w-full h-7 text-xs"
        onClick={() => onChange([...value, { key: '', value: '' }])}
      >
        <Plus className="h-3 w-3 mr-1" /> {addLabel}
      </Button>
    </div>
  );
}

// ── Live tools picker — agent narrow panel + MCP tool selector ──────
//
// Shared between renderAgentProps (multi-select) and renderToolProps
// (single-select). Pulls /workspace/tools so we never ship a stale
// hardcoded catalog (#F6, #F10).

function ToolsAgentPicker({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const { data, isLoading } = useWorkspaceTools({ active: 'true' });
  const rows = data?.data ?? [];
  const grouped = useMemo(() => {
    const m = new Map<string, typeof rows>();
    for (const r of rows) {
      const group = r.toolSource === 'builtin' ? 'Built-in'
        : r.toolSource === 'mcp' ? `MCP${r.namespace ? ` · ${r.namespace}` : ''}`
        : r.toolSource === 'openapi' ? 'OpenAPI'
        : r.toolSource === 'database' ? 'Database'
        : 'Custom';
      const arr = m.get(group) ?? [];
      arr.push(r);
      m.set(group, arr);
    }
    return Array.from(m.entries());
  }, [rows]);

  if (isLoading) {
    return <p className="text-[10px] text-muted-foreground py-2">Loading tools…</p>;
  }
  if (rows.length === 0) {
    return (
      <div className="border border-dashed rounded-md p-3 text-center">
        <Wrench className="h-4 w-4 mx-auto text-muted-foreground/40 mb-1" />
        <p className="text-[10px] text-muted-foreground">No tools registered for this tenant.</p>
        <Link to="/connections/marketplace" className="text-[10px] text-primary hover:underline">
          Connect a system →
        </Link>
      </div>
    );
  }
  return (
    <>
      <div className="space-y-2 max-h-[200px] overflow-auto border rounded-md p-1.5">
        {grouped.map(([group, tools]) => (
          <div key={group}>
            <p className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider px-1 pb-0.5">{group}</p>
            <div className="space-y-0.5">
              {tools.map((tool) => {
                const isSelected = selected.includes(tool.name);
                return (
                  <label
                    key={tool.id}
                    className={cn(
                      'flex items-center gap-1.5 px-2 py-1 rounded text-[10px] cursor-pointer transition-colors',
                      isSelected ? 'bg-primary/10 text-primary' : 'hover:bg-muted/50',
                    )}
                  >
                    <input
                      type="checkbox"
                      className="h-3 w-3 rounded"
                      checked={isSelected}
                      onChange={() => {
                        const next = isSelected
                          ? selected.filter((t) => t !== tool.name)
                          : [...selected, tool.name];
                        onChange(next);
                      }}
                    />
                    <span className="font-medium">{tool.displayName || tool.name}</span>
                    <span className="font-mono text-muted-foreground/60 ml-auto truncate">{tool.name}</span>
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <p className="text-[9px] text-muted-foreground mt-1">
        {selected.length} tool{selected.length !== 1 ? 's' : ''} selected
      </p>
    </>
  );
}

function ToolSinglePicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const { data, isLoading } = useWorkspaceTools({ active: 'true' });
  const rows = data?.data ?? [];
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) =>
      r.name.toLowerCase().includes(q) ||
      (r.displayName?.toLowerCase().includes(q) ?? false) ||
      r.description.toLowerCase().includes(q));
  }, [rows, search]);

  if (isLoading) {
    return <p className="text-[10px] text-muted-foreground py-2">Loading tools…</p>;
  }
  if (rows.length === 0) {
    return (
      <div className="border border-dashed rounded-md p-3 text-center">
        <Wrench className="h-4 w-4 mx-auto text-muted-foreground/40 mb-1" />
        <p className="text-[10px] text-muted-foreground">No tools registered for this tenant.</p>
        <Link to="/connections/marketplace" className="text-[10px] text-primary hover:underline">
          Connect a system →
        </Link>
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      <Input
        className="h-7 text-xs"
        placeholder="Search tools..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div className="max-h-[180px] overflow-auto border rounded-md">
        {filtered.map((tool) => {
          const selected = value === tool.name;
          return (
            <button
              key={tool.id}
              type="button"
              onClick={() => onChange(tool.name)}
              className={cn(
                'flex items-start gap-2 px-2 py-1 w-full text-left transition-colors text-[10px]',
                selected ? 'bg-primary/10 text-primary' : 'hover:bg-muted/40',
              )}
            >
              <Wrench className="h-3 w-3 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="font-medium truncate">{tool.displayName || tool.name}</p>
                <p className="font-mono text-muted-foreground/60 truncate">{tool.name}</p>
              </div>
            </button>
          );
        })}
        {filtered.length === 0 && (
          <p className="text-[10px] text-muted-foreground text-center py-3">No tools match "{search}"</p>
        )}
      </div>
    </div>
  );
}

// ── Error handler — fallback workflow + specific-node picker
// (#error_handler audit)

function ErrorHandlerProps({
  config,
  updateConfig,
}: {
  config: Record<string, unknown>;
  updateConfig: (key: string, value: unknown) => void;
}) {
  const errorSource = (config.errorSource as string) ?? 'workflow';
  const action = (config.action as string) ?? 'continue';
  const { data: workflows = [] } = useWorkflows();

  return (
    <>
      <FieldGroup label="Error source">
        <Select value={errorSource} onValueChange={(v) => updateConfig('errorSource', v)}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="workflow">Any node in this workflow</SelectItem>
            <SelectItem value="specific">Specific node ID</SelectItem>
            <SelectItem value="error_kind">Specific error type</SelectItem>
          </SelectContent>
        </Select>
      </FieldGroup>
      {errorSource === 'specific' && (
        <FieldGroup
          label="Watched node ID"
          help={<span className="text-[9px] text-muted-foreground">Right-click any node on the canvas → Copy ID. The handler only fires on errors from this node.</span>}
        >
          <Input
            className="h-8 text-xs font-mono"
            value={(config.specificNodeId as string) ?? ''}
            onChange={(e) => updateConfig('specificNodeId', e.target.value)}
            placeholder="node-id-from-canvas"
          />
        </FieldGroup>
      )}
      {errorSource === 'error_kind' && (
        <FieldGroup label="Error code/kind">
          <Input
            className="h-8 text-xs font-mono"
            value={(config.errorKind as string) ?? ''}
            onChange={(e) => updateConfig('errorKind', e.target.value)}
            placeholder="EXECUTION_TIMEOUT"
          />
        </FieldGroup>
      )}
      <FieldGroup label="Action on error">
        <Select value={action} onValueChange={(v) => updateConfig('action', v)}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="continue">Continue with error data</SelectItem>
            <SelectItem value="retry">Retry failed node</SelectItem>
            <SelectItem value="fallback">Run fallback workflow</SelectItem>
            <SelectItem value="notify">Notify and stop</SelectItem>
          </SelectContent>
        </Select>
      </FieldGroup>
      {action === 'retry' && (
        <>
          <FieldGroup label="Max retries">
            <Input
              className="h-8 text-xs"
              type="number"
              min={0}
              max={10}
              value={(config.maxRetries as number) ?? 3}
              onChange={(e) => updateConfig('maxRetries', parseInt(e.target.value) || 0)}
            />
          </FieldGroup>
          <FieldGroup label="Backoff strategy">
            <Select value={(config.backoff as string) ?? 'exponential'} onValueChange={(v) => updateConfig('backoff', v)}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="immediate">Immediate (no delay)</SelectItem>
                <SelectItem value="linear">Linear (Nx initial delay)</SelectItem>
                <SelectItem value="exponential">Exponential (2^N x initial delay)</SelectItem>
              </SelectContent>
            </Select>
          </FieldGroup>
          <FieldGroup label="Initial delay (seconds)">
            <Input
              className="h-8 text-xs"
              type="number"
              min={0}
              max={300}
              value={(config.initialDelaySec as number) ?? 1}
              onChange={(e) => updateConfig('initialDelaySec', parseInt(e.target.value) || 0)}
            />
          </FieldGroup>
        </>
      )}
      {action === 'fallback' && (
        <FieldGroup
          label="Fallback workflow"
          help={<span className="text-[9px] text-muted-foreground">Run this workflow with the error context as input.</span>}
        >
          {workflows.length === 0 ? (
            <p className="text-[10px] text-muted-foreground">No workflows available — create one to use as a fallback.</p>
          ) : (
            <Select
              value={(config.fallbackWorkflowId as string) ?? ''}
              onValueChange={(v) => updateConfig('fallbackWorkflowId', v)}
            >
              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select workflow..." /></SelectTrigger>
              <SelectContent>
                {workflows
                  .filter((w) => w.status !== 'archived')
                  .map((w) => (
                    <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                  ))}
              </SelectContent>
            </Select>
          )}
        </FieldGroup>
      )}
      {action === 'notify' && (
        <FieldGroup label="Notify channel">
          <Select value={(config.notifyChannel as string) ?? 'email'} onValueChange={(v) => updateConfig('notifyChannel', v)}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="email">Email</SelectItem>
              <SelectItem value="slack">Slack</SelectItem>
              <SelectItem value="teams">Microsoft Teams</SelectItem>
              <SelectItem value="in_app">In-app</SelectItem>
            </SelectContent>
          </Select>
        </FieldGroup>
      )}
    </>
  );
}

// ── Sub-workflow picker — pulls live workflows so the user can only
// pick one that actually exists. Was a hardcoded list of 4 fake IDs.

function SubWorkflowProps({
  config,
  updateConfig,
}: {
  config: Record<string, unknown>;
  updateConfig: (key: string, value: unknown) => void;
}) {
  const { data: workflows = [], isLoading } = useWorkflows();
  const selectedId = (config.workflowId as string) ?? '';
  const selectedName = workflows.find((w) => w.id === selectedId)?.name;

  return (
    <>
      <FieldGroup
        label="Workflow to invoke"
        help={<span className="text-[9px] text-muted-foreground">Pulls live from this tenant's workflows. The selected workflow will run as a child of this one.</span>}
      >
        {isLoading ? (
          <p className="text-[10px] text-muted-foreground py-2">Loading workflows…</p>
        ) : workflows.length === 0 ? (
          <div className="border border-dashed rounded-md p-3 text-center">
            <p className="text-[10px] text-muted-foreground">
              No workflows in this tenant yet. Create one from the workflows list before referencing it from a sub-workflow node.
            </p>
          </div>
        ) : (
          <Select value={selectedId} onValueChange={(v) => updateConfig('workflowId', v)}>
            <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select workflow..." /></SelectTrigger>
            <SelectContent>
              {workflows
                .filter((w) => w.status !== 'archived')
                .map((w) => (
                  <SelectItem key={w.id} value={w.id}>
                    {w.name}
                    <span className="ml-1 text-[9px] text-muted-foreground">({w.status})</span>
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        )}
        {selectedName && selectedId === config.workflowId && (
          <p className="text-[9px] text-muted-foreground mt-1">
            Resolved: <span className="font-mono">{selectedName}</span>
          </p>
        )}
      </FieldGroup>
      <FieldGroup label="Input Parameters">
        <Textarea
          className="text-xs font-mono min-h-[60px]"
          value={(config.inputParams as string) ?? ''}
          onChange={(e) => updateConfig('inputParams', e.target.value)}
          placeholder='{"assetId": "{{$input.assetId}}"}'
        />
        <p className="text-[9px] text-muted-foreground mt-1">JSON object passed as the trigger payload to the sub-workflow.</p>
      </FieldGroup>
      <FieldGroup label="Execution Mode">
        <Select
          value={(config.executionMode as string) ?? 'wait'}
          onValueChange={(v) => updateConfig('executionMode', v)}
        >
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="wait">Wait for completion (synchronous)</SelectItem>
            <SelectItem value="fire_and_forget">Fire and forget (asynchronous)</SelectItem>
          </SelectContent>
        </Select>
      </FieldGroup>
    </>
  );
}
